/**
 * Be Inspired page Web Components.
 *
 * Mirrors nulo-oxygen:
 *   - VideoBeInspired.jsx  → inline video autoplay loop between start/end time
 *   - SectionInspiredCrouselTemp.jsx → Swiper carousel + accordion
 */

const IS_DESKTOP_MQ = window.matchMedia('(min-width: 768px)');

/* ─────────────────────────────────────────────────────────────
   VideoBeInspiredController
   Attached to each slide's <video> element. Handles autoplay
   between video_start_time → video_end_time, looping, tab
   visibility, user play/pause toggle.
   ───────────────────────────────────────────────────────────── */
class VideoBeInspiredController {
  /** @param {HTMLVideoElement} el @param {number} start @param {number} end */
  constructor(el, start, end) {
    this._el = el;
    this._start = parseInt(start, 10) || 0;
    this._end = parseInt(end, 10) || 0;
    this._loopTimer = null;
    this._retryTimer = null;
    this._userPaused = false;
    this._tabVisible = true;

    this._isDesktop = IS_DESKTOP_MQ.matches;
    this._mqListener = (e) => {
      this._isDesktop = e.matches;
      if (!this._isDesktop) this._clearTimers();
      else if (!this._userPaused) this._startAutoplay();
    };
    IS_DESKTOP_MQ.addEventListener('change', this._mqListener);

    this._onVisibility = () => {
      this._tabVisible = !document.hidden;
      if (!document.hidden && this._isDesktop && !this._userPaused) {
        this._clearTimers();
        setTimeout(() => this._startAutoplay(), 500);
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);

    el.addEventListener('loadedmetadata', () => {
      if (this._isDesktop) this._startAutoplay();
    });
    el.addEventListener('ended', () => {
      if (!this._userPaused) this._startAutoplay();
    });

    // If metadata already ready
    if (el.readyState >= 1 && this._isDesktop) this._startAutoplay();
  }

  _clearTimers() {
    clearTimeout(this._loopTimer);
    clearTimeout(this._retryTimer);
    this._loopTimer = null;
    this._retryTimer = null;
  }

  _startAutoplay(retry = 0) {
    const el = this._el;
    if (!el || !this._start || !this._end) return;
    this._userPaused = false;
    el.currentTime = this._start;

    const delay = (this._end - this._start) * 1000;

    const doPlay = () => {
      el.play().catch((err) => {
        if (err.name === 'AbortError' && retry < 3 && this._tabVisible) {
          this._retryTimer = setTimeout(
            () => this._startAutoplay(retry + 1),
            1000 * (retry + 1),
          );
        }
      });
    };

    setTimeout(doPlay, 100);

    const loop = () => {
      if (!this._userPaused && this._tabVisible) {
        el.currentTime = this._start;
        el.play().catch(() => {
          setTimeout(() => {
            if (!this._userPaused && this._tabVisible) loop();
          }, 2000);
        });
        this._loopTimer = setTimeout(loop, delay);
      }
    };
    this._loopTimer = setTimeout(loop, delay);
  }

  togglePlayPause() {
    if (this._userPaused) {
      this._clearTimers();
      this._startAutoplay();
    } else {
      this._userPaused = true;
      this._clearTimers();
      this._el.pause();
    }
    return this._userPaused;
  }

  get isPaused() {
    return this._userPaused;
  }

  destroy() {
    this._clearTimers();
    IS_DESKTOP_MQ.removeEventListener('change', this._mqListener);
    document.removeEventListener('visibilitychange', this._onVisibility);
  }
}

/* ─────────────────────────────────────────────────────────────
   <nulo-be-inspired-hero> Web Component
   ───────────────────────────────────────────────────────────── */
class NuloBeInspiredHero extends HTMLElement {
  connectedCallback() {
    this._initAccordion();
    this._initCarousel();
  }

  disconnectedCallback() {
    this._videoControllers?.forEach((c) => c.destroy());
    this._swiper?.destroy(true, true);
  }

  /* ── accordion ── */
  _initAccordion() {
    const trigger = this.querySelector('[data-acc-trigger]');
    const body = this.querySelector('[data-acc-body]');
    const iconOpen = this.querySelector('[data-acc-icon-open]');
    const iconClose = this.querySelector('[data-acc-icon-close]');
    if (!trigger || !body) return;

    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!expanded));
      if (expanded) {
        body.setAttribute('hidden', '');
        iconOpen?.setAttribute('hidden', '');
        iconClose?.removeAttribute('hidden');
      } else {
        body.removeAttribute('hidden');
        iconOpen?.removeAttribute('hidden');
        iconClose?.setAttribute('hidden', '');
      }
    });
  }

  /* ── carousel + video ── */
  _initCarousel() {
    const swiperEl = this.querySelector('[data-swiper]');
    if (!swiperEl || typeof Swiper === 'undefined') return;

    const prevBtn = this.querySelector('[data-carousel-prev]');
    const nextBtn = this.querySelector('[data-carousel-next]');

    // swiper-bundle already includes Navigation + Pagination modules
    this._swiper = new Swiper(swiperEl, {
      loop: false,
      speed: 500,
      slidesPerView: 1.2,
      slidesPerGroup: 1,
      watchSlidesProgress: true,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      breakpoints: {
        480: { slidesPerView: 1.2 },
        767: { slidesPerView: 2.2 },
        1200: { slidesPerView: 2.5 },
        1450: { slidesPerView: 2.3 },
      },
      on: {
        resize: () => this._swiper?.update(),
      },
    });

    /* Init video controllers */
    this._videoControllers = [];
    this.querySelectorAll('[data-bi-slide]').forEach((slide, idx) => {
      const video = slide.querySelector('video[data-bi-video]');
      if (!video) return;
      const start = video.dataset.startTime;
      const end = video.dataset.endTime;
      const ctrl = new VideoBeInspiredController(video, start, end);
      this._videoControllers[idx] = ctrl;

      /* play/pause button */
      const btn = slide.querySelector('[data-play-pause]');
      if (!btn) return;
      const iconPlay = btn.querySelector('[data-icon-play]');
      const iconPause = btn.querySelector('[data-icon-pause]');
      btn.addEventListener('click', () => {
        const nowPaused = ctrl.togglePlayPause();
        if (nowPaused) {
          iconPlay?.removeAttribute('hidden');
          iconPause?.setAttribute('hidden', '');
          btn.setAttribute('aria-label', btn.dataset.labelPlay || 'Play');
          btn.classList.add('bi-play-now');
          btn.classList.remove('bi-pause-now');
        } else {
          iconPlay?.setAttribute('hidden', '');
          iconPause?.removeAttribute('hidden');
          btn.setAttribute('aria-label', btn.dataset.labelPause || 'Pause');
          btn.classList.add('bi-pause-now');
          btn.classList.remove('bi-play-now');
        }
      });
    });
  }
}

if (!customElements.get('nulo-be-inspired-hero')) {
  customElements.define('nulo-be-inspired-hero', NuloBeInspiredHero);
}
